import {
    MORE_VALUE,
    areasFromOptions,
    destinationTiers,
    rankAttribute,
    rankedTiers,
    selectionState,
    showMoreLabel,
} from "@shared/rankedAreas";

/**
 * Rank-tier parking-area selectors on Select2, the one component of the booking form on the user side (web) and on
 * the admin side (booking for a user): best tier first, a «more» sentinel for the others that expands the list and
 * keeps it open, no silent replacement of an area that is no longer offered. Select2 and its per-instance colours
 * are already loaded by both themes (user side: Select2's bootstrap4 theme, settings.theme), so the component adds no
 * CSS. Uses the global jQuery of the page.
 *
 * @typedef {{id: string, text: string, selected: boolean, more: boolean, group: (string|null),
 *          rank: (string|undefined)}} OptionData group is the heading (optgroup label) the option is listed under,
 *          null for a flat option; rank is the option's data-rank (rankAttribute), undefined for the placeholder, the
 *          sentinel and unranked areas
 */

/**
 * With a second tier the list has the two headings: expanded they hold the two tiers; collapsed the first holds the
 * best tier and the second only the «more» entry, so the user sees at once that the others exist and where they go.
 * Without a second tier (flag off, no ranks, a single rank) the list stays flat. A placeholder stays outside them.
 *
 * @param {{tiers: {primary: Object[], others: Object[], showMore: boolean}, state: string, expanded: boolean,
 *          current: string, moreLabel: string, placeholderLabel: string,
 *          groupLabels: {primary: string, others: string}}} model
 * @returns {OptionData[]}
 */
export function buildOptions(model) {
    const options = [];
    if (model.state === "missing" && model.current === "") {
        options.push({id: "", text: model.placeholderLabel, selected: true, more: false, group: null});
    }
    const grouped = model.tiers.others.length > 0;
    const pushAreas = (areas, group) => areas.forEach((area) => options.push(
        {
            id: area.id,
            text: area.name,
            selected: area.id === model.current,
            more: false,
            group,
            rank: rankAttribute(area),
        }
    ));
    pushAreas(model.tiers.primary, grouped ? model.groupLabels.primary : null);
    if (model.expanded) {
        pushAreas(model.tiers.others, grouped ? model.groupLabels.others : null);
    }
    if (model.tiers.showMore && !model.expanded) {
        const text = showMoreLabel(model.moreLabel, model.tiers.others.length);
        options.push({id: MORE_VALUE, text, selected: false, more: true, group: model.groupLabels.others});
    }
    return options;
}

/**
 * Wraps Select2's own matcher (diacritics-insensitive) with the only rule of this selector: the sentinel always
 * stays. Only options present in the list are ever offered, so searching never expands the hidden tiers.
 *
 * Groups are walked here and not by Select2: its matcher recurses into a group's children with itself, not with the
 * configured matcher, and would drop the sentinel listed under the second heading. A group keeps the children that
 * match; with none it falls back, as Select2 does, to its own label.
 *
 * @param {function(Object, Object): (Object|null)} baseMatcher Select2's default matcher
 * @returns {function(Object, Object): (Object|null)}
 */
export function createMatcher(baseMatcher) {
    const matcher = (params, data) => {
        if (data.id === MORE_VALUE) {
            return data;
        }
        if (!data.children || data.children.length === 0) {
            return baseMatcher(params, data);
        }
        const children = data.children.map((child) => matcher(params, child)).filter((child) => child !== null);
        if (children.length > 0) {
            return {...data, children};
        }
        return baseMatcher(params, {...data, children: []});
    };
    return matcher;
}

const SVG_NAMESPACE = "http://www.w3.org/2000/svg";

// Bootstrap Icons chevron-down (MIT), the path Bootstrap 5's own CSS uses for the accordion. The admin theme loads no
// icon font with it, and a text glyph (⌄) renders like a small «v».
const CHEVRON_DOWN_PATH = "M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l"
    + "-6-6a.5.5 0 0 1 0-.708z";

/** A down chevron the size of the text, in its colour, hidden from screen readers. */
function chevronDown() {
    const icon = document.createElementNS(SVG_NAMESPACE, "svg");
    icon.setAttribute("viewBox", "0 0 16 16");
    icon.setAttribute("width", "1em");
    icon.setAttribute("height", "1em");
    icon.setAttribute("fill", "currentColor");
    icon.setAttribute("aria-hidden", "true");
    icon.setAttribute("focusable", "false");
    // Inline, not ms-1: the user side runs Bootstrap 4 (ml-1), the admin side Bootstrap 5.
    icon.style.marginInlineStart = "0.25rem";
    icon.style.verticalAlign = "-0.125em";
    const path = document.createElementNS(SVG_NAMESPACE, "path");
    path.setAttribute("fill-rule", "evenodd");
    path.setAttribute("d", CHEVRON_DOWN_PATH);
    icon.append(path);
    return icon;
}

/**
 * Select2 result of the «more» entry, like the app's list action: centred, semibold, a thin line above it that
 * separates it from the areas, and a down chevron. It keeps the normal text colour: the primary colour of some instance
 * themes is below 4.5:1 on white (WCAG AA). Bootstrap 5.1 has no fw-semibold, hence the inline weight. Mouse hover and
 * keyboard focus get Select2's own highlight of the option row, as every other option. Screen readers read only the
 * label: the chevron is aria-hidden. Built with a text node: the label is never read as HTML.
 *
 * @param {string} text the composed label
 * @returns {HTMLSpanElement}
 */
export function moreResult(text) {
    const result = document.createElement("span");
    result.className = "ranked-area-more d-block text-center border-top pt-1";
    result.style.fontWeight = "600";
    result.append(document.createTextNode(text), chevronDown());
    return result;
}

const attached = new WeakMap();

/**
 * Rewrites the options of the select, consecutive options of the same heading inside one optgroup (Select2 reads
 * them as groups and its matcher searches inside them), and tells Select2 about the new selection.
 */
function replaceOptions(select, options) {
    select.replaceChildren();
    let group = null;
    options.forEach((data) => {
        const element = document.createElement("option");
        element.value = data.id;
        element.textContent = data.text;
        element.selected = data.selected;
        if (data.rank !== undefined) {
            element.dataset.rank = data.rank;
        }
        if (data.group === null) {
            group = null;
            select.append(element);
            return;
        }
        if (group === null || group.label !== data.group) {
            group = document.createElement("optgroup");
            group.label = data.group;
            select.append(group);
        }
        group.append(element);
    });
    globalThis.$(select).trigger("change.select2");
}

/**
 * Select2 with the sentinel styled and kept by searches. onMore runs instead of selecting the sentinel and must
 * rebuild the options; the list is then reopened on them. onPick receives every other selected id.
 */
function initSelect2(select, settings, onMore, onPick) {
    const $ = globalThis.$;
    const options = {
        width: "100%",
        matcher: createMatcher($.fn.select2.defaults.defaults.matcher),
        templateResult: (data) => (data.id === MORE_VALUE ? moreResult(data.text) : data.text),
    };
    // User side: the dropdown lives in the field's own container, so it moves with the field when the booking modal
    // scrolls. On the body it stays where it opened, and with the .modal as parent Select2 4.0 misplaces it once the
    // modal is scrolled (checked in the browser).
    if (settings.dropdownParent) {
        options.dropdownParent = settings.dropdownParent;
    }
    // The user-side instance themes colour Select2's bootstrap4 theme; the admin header sets bootstrap-5 as the
    // default.
    if (settings.theme) {
        options.theme = settings.theme;
    }
    $(select).select2(options);

    $(select).on("select2:selecting", (event) => {
        if (event.params.args.data.id !== MORE_VALUE) {
            onPick(event.params.args.data.id);
            return;
        }
        event.preventDefault();
        onMore();
        // The prevented selection leaves the dropdown open and open() is a no-op then: close first so the results
        // are queried again from the rebuilt options.
        $(select).select2("close");
        $(select).select2("open");
    });
}

/**
 * Idempotent per select element: the form handler can run again on the same DOM (the select is then already
 * rewritten and carries the user's choice), while a new element is attached fresh.
 *
 * With a hintLabel (admin side) a muted hint under the field says, while the tiers are active (a «more» entry
 * exists), that the order is the user's, not the operator's.
 *
 * @param {HTMLSelectElement} select
 * @param {{rankEnabled: boolean, requestedId: string,
 *          moreLabel: string, missingMessage: string, placeholderLabel: string, hintLabel: (string|undefined),
 *          groupLabels: {primary: string, others: string}, dropdownParent: (Object|undefined),
 *          theme: (string|undefined)}} settings
 */
export function attachSelect2(select, settings) {
    if (attached.has(select)) {
        return attached.get(select);
    }
    const tiers = rankedTiers(areasFromOptions(select.options), settings.rankEnabled);
    const state = selectionState(tiers, settings.requestedId);
    let expanded = state === "secondary";
    let current = (state === "primary" || state === "secondary") ? String(settings.requestedId) : "";
    const feedback = document.createElement("div");
    feedback.className = "invalid-feedback d-block ranked-area-missing";
    feedback.textContent = settings.missingMessage;

    function render() {
        const model = {
            tiers,
            state,
            expanded,
            current,
            moreLabel: settings.moreLabel,
            placeholderLabel: settings.placeholderLabel,
            groupLabels: settings.groupLabels,
        };
        const options = buildOptions(model);
        if (current === "" && state !== "missing" && options.length) {
            current = options[0].more ? "" : options[0].id;
            options[0].selected = options[0].id === current;
        }
        replaceOptions(select, options);
    }

    render();
    initSelect2(select, settings, () => {
        expanded = true;
        render();
    }, (id) => {
        current = id;
        feedback.remove();
    });

    const container = select.nextElementSibling;
    const field = container && container.classList.contains("select2-container") ? container : select;
    if (tiers.showMore && settings.hintLabel) {
        const hint = document.createElement("div");
        hint.className = "form-text ranked-area-hint";
        hint.textContent = settings.hintLabel;
        field.after(hint);
    }
    if (state === "missing") {
        field.after(feedback);
    }

    const controller = {expand: () => { expanded = true; render(); }};
    attached.set(select, controller);
    return controller;
}

/**
 * Rank-tier destination selector for Select2. The handler owns the allowed set (A2B clusters of the departure) and
 * hands it over with setAllowed; the list then holds only the allowed areas. Same sentinel, search and idempotence as
 * the departure. A booking's destination the list does not offer stays unselected under a placeholder, with the
 * message under the field, until the user picks an area or a departure change selects one; meanwhile selectedId()
 * returns it, so a clusters response of the first load does not replace it either.
 *
 * @param {HTMLSelectElement} select
 * @param {{rankEnabled: boolean, requestedId: string, moreLabel: string, missingMessage: string,
 *          placeholderLabel: string, groupLabels: {primary: string, others: string},
 *          dropdownParent: (Object|undefined), theme: (string|undefined)}} settings
 * @returns {{setAllowed: function((Set<string>|null), string): void, selectedId: function(): string}}
 */
export function attachDestinationSelect2(select, settings) {
    if (attached.has(select)) {
        return attached.get(select);
    }
    const areas = areasFromOptions(select.options);
    let view = destinationTiers(areas, null, settings.rankEnabled, settings.requestedId);
    const feedback = document.createElement("div");
    feedback.className = "invalid-feedback d-block ranked-area-missing";
    feedback.textContent = settings.missingMessage;

    /** Below the Select2 container once Select2 is on, like the departure's message. */
    function showFeedback() {
        if (view.missingId === "") {
            feedback.remove();
            return;
        }
        const container = select.nextElementSibling;
        const field = container && container.classList.contains("select2-container") ? container : select;
        field.after(feedback);
    }

    function render() {
        replaceOptions(select, buildOptions({
            tiers: view.tiers,
            state: view.missingId !== "" ? "missing" : selectionState(view.tiers, view.current),
            expanded: view.expanded,
            current: view.current,
            moreLabel: settings.moreLabel,
            placeholderLabel: settings.placeholderLabel,
            groupLabels: settings.groupLabels,
        }));
    }

    render();
    initSelect2(select, settings, () => {
        view = {...view, expanded: true};
        render();
    }, (id) => {
        if (id !== "") {
            view = {...view, current: id, missingId: ""};
            feedback.remove();
        }
    });
    showFeedback();

    const controller = {
        setAllowed: (allowedIds, selectedId) => {
            view = destinationTiers(areas, allowedIds, settings.rankEnabled, selectedId);
            render();
            showFeedback();
        },
        selectedId: () => view.missingId || view.current,
    };
    attached.set(select, controller);
    return controller;
}

/** Teardown of the booking form: unbinds the handler, destroys Select2 and allows a later fresh attach. */
export function detachSelect2(select) {
    const $ = globalThis.$;
    $(select).off("select2:selecting");
    $(select).select2("destroy");
    attached.delete(select);
}
